<template>
  <v-card class="elevation-12" :loading="loading">
    <v-toolbar
        color="primary"
        dark
        flat
    >
      <v-toolbar-title>{{ $t('addGlobalUserForm.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>
    </v-toolbar>
    <v-card-text>
      <v-form>
        <v-text-field
            v-model="form.fullname"
            :label="$t('common.labels.fullName')"
            type="text"
            maxlength="400"
            :counter="400"
            :error-messages="fullnameErrors"
        />

        <v-text-field
            v-model="form.username"
            :label="$t('common.labels.login')"
            type="text"
            maxlength="12"
            :counter="12"
            :error-messages="usernameErrors"
            @keyup="form.isUsernameExists = false"
        />

        <v-text-field
            v-model="form.password"
            :label="$t('common.labels.password')"
            type="password"
            :error-messages="passwordErrors"
        />

        <v-text-field
            v-model="form.passwordRepeat"
            :label="$t('common.labels.passwordRepeat')"
            type="password"
            :error-messages="passwordRepeatErrors"
        />

        <v-combobox v-model="form.unit" :items="units" :label="$t('common.labels.department')" item-text="name"
                    :error-messages="unitErrors"/>

        <v-checkbox
            v-model="form.canSignOnBehalfOfOrganization"
            :label="$t('addGlobalUserForm.canSign')"
            color="red darken-3"
        ></v-checkbox>

        <v-radio-group v-model="form.type">
          <template v-slot:label>
            <div><strong>{{ $t('addGlobalUserForm.userType') }}</strong></div>
          </template>
          <v-radio :value="1" color="error">
            <template v-slot:label>
              <div>{{ $t('userTypes.admin') }}</div>
            </template>
          </v-radio>
          <v-radio :value="3" color="success">
            <template v-slot:label>
              <div>{{ $t('userTypes.controller') }}</div>
            </template>
          </v-radio>
          <v-radio :value="4" color="info">
            <template v-slot:label>
              <div>{{ $t('userTypes.monitoring') }}</div>
            </template>
          </v-radio>
          <v-radio :value="5" color="info">
            <template v-slot:label>
              <div>{{ $t('userTypes.auditor') }}</div>
            </template>
          </v-radio>
        </v-radio-group>

        <v-snackbar v-model="form.isUsernameExists"
                    :timeout="5000"
                    :top="true"
                    color="error"
        >
          {{ $t('addGlobalUserForm.userExists') }}
        </v-snackbar>
        <v-snackbar v-model="isServerError"
                    :timeout="5000"
                    :top="true"
                    color="error"
        >
          {{ $t('common.messages.serverError') }}
        </v-snackbar>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn text color="secondary" @click="onCancelButtonClicked">{{ $t('common.actions.cancel') }}</v-btn>
      <v-spacer/>
      <v-btn text color="primary" @click="onAddButtonClicked">{{ $t('common.actions.add') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {maxLength, minLength, numeric, required, sameAs} from "vuelidate/lib/validators";
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP} from "@/store/modules/layout/actions";
import _ from "underscore";
import Mappers from "@/mappers";
import GlobalUnitService from "@/services/global.unit.service";
import GlobalUserService from "@/services/global.user.service";

export default {
  name: "AddAdminGlobalUserComponent",
  props: {
    inn: String
  },
  data() {
    return {
      loading: false,
      form: {
        fullname: '',
        username: '998',
        password: '',
        passwordRepeat: '',
        type: '',
        unit: null,

        isUsernameExists: false,
        canSignOnBehalfOfOrganization: false
      },
      units: [],
      isServerError: false,
    }
  },
  computed: {
    fullnameErrors() {
      const errors = []

      if (this.$v.form.fullname.$dirty) {
        if (!this.$v.form.fullname.required) {
          errors.push(this.$t('common.errors.fullNameRequired'))
        } else {
          !this.$v.form.fullname.maxLength && errors.push(this.$t('common.errors.fullNameTooLong'))
        }
      }

      return errors
    },
    usernameErrors() {
      const errors = []
      if (this.$v.form.username.$dirty) {
        if (!this.$v.form.username.required) {
          errors.push(this.$t('common.errors.phoneRequired'))
        } else {
          if (!this.$v.form.username.numeric || !this.$v.form.username.format)
            errors.push(this.$t('common.errors.phoneFormat'))
          if (!this.$v.form.username.minLength || !this.$v.form.username.maxLength)
            errors.push(this.$t('common.errors.phoneLength'))
        }

        this.form.isUsernameExists && errors.push(this.$t('common.errors.loginExists'))
      }

      return errors
    },
    passwordErrors() {
      const errors = []

      if (this.$v.form.password.$dirty) {
        if (!this.$v.form.password.required) {
          errors.push(this.$t('common.errors.passwordRequired'))
        } else {
          if (!this.$v.form.password.minLength)
            errors.push(this.$t('common.errors.passwordTooShort'))
        }
      }

      return errors
    },
    passwordRepeatErrors() {
      const errors = []

      if (this.$v.form.passwordRepeat.$dirty) {
        if (!this.$v.form.passwordRepeat.required) {
          errors.push(this.$t('common.errors.passwordRepeatRequired'))
        } else {
          if (!this.$v.form.passwordRepeat.sameAs) {
            errors.push(this.$t('common.errors.passwordsDontMatch'))
          } else if (!this.$v.form.password.minLength) {
            errors.push(this.$t('common.errors.passwordTooShort'))
          }
        }
      }

      return errors
    },
    unitErrors() {
      const errors = []

      if (this.$v.form.unit.$dirty)
        if (!this.$v.form.unit.required)
          errors.push(this.$t('common.errors.departmentRequired'))

      return errors
    }
  },
  validations: {
    form: {
      fullname: {
        required,
        maxLength: maxLength(400)
      },
      username: {
        required,
        maxLength: maxLength(12),
        minLength: minLength(12),
        numeric,
        format(value) {
          return /^998\d{9}$/.test(value);
        }
      },
      password: {
        required,
        minLength: minLength(8),
      },
      passwordRepeat: {
        required,
        sameAs: sameAs(function () {
          return this.form.password
        })
      },
      unit: {
        required
      }
    }
  },
  methods: {
    init() {
      this.clear()
      this.loadUnits()
    },

    clear() {
      this.$v.$reset()
      this.form.fullname = ''
      this.form.username = '998'
      this.form.password = ''
      this.form.passwordRepeat = ''
      this.form.type = ''

      this.form.isUsernameExists = false
    },
    loadUnits() {
      let selectedOrganizationInn = this.inn;
      GlobalUnitService.getUserUnits({inn: selectedOrganizationInn})
          .then(response => {
            this.units = _.map(response.data, o => {
              return Mappers.mapGlobalUnit(o)
            })
          })
    },

    onCancelButtonClicked() {
      this.clear()
      this.$emit('cancel')
    },
    onAddButtonClicked() {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        this.loading = true;
        this.$store.dispatch(LAYOUT_LOADING_START)

        this.form.isUsernameExists = false
        this.isServerError = false

        GlobalUserService.checkUsername(this.form.username)
            .then(response => {
              if (response.data.IsExists) {
                this.form.isUsernameExists = true
                this.loading = false
                this.$store.dispatch(LAYOUT_LOADING_STOP)
              } else {
                GlobalUserService.addUser({
                  userFullName: this.form.fullname,
                  username: this.form.username,
                  password: this.form.password,
                  type: this.form.type,
                  unit: this.form.unit,
                  canSignOnBehalfOfOrganization: this.form.canSignOnBehalfOfOrganization
                })
                    .then(() => {
                      this.clear()
                      this.$emit('added')
                    }, () => {
                      this.isServerError = true
                    })
                    .finally(() => {
                      this.loading = false
                      this.$store.dispatch(LAYOUT_LOADING_STOP)
                    })
              }
            }, () => {
              this.loading = false
              this.$store.dispatch(LAYOUT_LOADING_STOP)
            })
      }
    }
  }
}
</script>

<style scoped>

</style>